import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { getGetStoresQueryOptions, useLogin, type loginResponse200 } from "@/api";
import { LoginCredentialTemplate } from "@/components/Templates";
import { getErrorMessage } from "@/lib/api/errors";
import { loginFormSchema, type LoginFormValues } from "@/lib/validation/auth.schemas";
import { useAuthStore } from "@/stores/auth.store";
import { consumePostLoginRedirect } from "@/lib/auth/postLoginRedirect";
import { useLoginPrefillStore } from "@/stores/loginPrefill.store";

export function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirect: redirectTo } = useSearch({ from: "/_auth/login" });
  const setAuth = useAuthStore((state) => state.setAuth);
  const [prefill] = useState(() => useLoginPrefillStore.getState().consumePrefill());

  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      username: prefill.phone,
      password: prefill.password,
      rememberMe: false,
    },
  });

  const { mutate: loginMutate, isPending } = useLogin<loginResponse200>({
    mutation: {
      onSuccess: async (res, variables) => {
        if (res.status !== 200) return;
        setAuth({
          accessToken: res.data.data.access_token,
          user: { username: variables.data.username },
        });
        await queryClient.prefetchQuery(getGetStoresQueryOptions());
        navigate({ to: redirectTo || consumePostLoginRedirect() || "/" });
      },
      onError: (error) => setServerError(getErrorMessage(error, "ورود انجام نشد؛ دوباره تلاش کنید")),
    },
  });

  const onSubmit = ({ username, password }: LoginFormValues) => {
    setServerError(null);
    loginMutate({ data: { username, password } });
  };

  return (
    <LoginCredentialTemplate
      register={register}
      control={control}
      handleSubmit={handleSubmit}
      onSubmit={onSubmit}
      errors={errors}
      isPending={isPending}
      serverError={serverError}
    />
  );
}
